/**
 * Trusscore Estimator - Screw Calculations
 *
 * NEW FEATURE: Calculates screw requirements for panel and trim installation.
 *
 * Screw Rules:
 * - Trim screws: 1 screw per 24 inches (2 feet) of trim
 * - Panel screws: 1 screw per 2 square feet of panel area
 */

import { MaterialItem, BillOfMaterials, ScrewRequirement } from '../types';
import {
  SCREWS_PER_TRIM_FOOT,
  SCREWS_PER_PANEL_SQFT,
  TRIM_STOCK_LENGTH,
} from './constants';

/**
 * Calculate total linear feet of trim from material items.
 *
 * @param trimItems - Array of trim material items
 * @returns Total linear feet of trim
 */
export function calculateTotalTrimLength(trimItems: MaterialItem[]): number {
  return trimItems.reduce((total, item) => {
    // Each trim piece is TRIM_STOCK_LENGTH feet long
    return total + item.quantity * item.lengthFeet;
  }, 0);
}

/**
 * Calculate total square footage from panel material items.
 *
 * @param panelItems - Array of panel material items
 * @returns Total square footage of panels
 */
export function calculateTotalPanelSqft(panelItems: MaterialItem[]): number {
  return panelItems.reduce((total, item) => {
    return total + item.quantity * item.sqft;
  }, 0);
}

/**
 * Calculate screws required for trim installation.
 *
 * Rule: 1 screw per 24 inches (2 feet) of trim
 *
 * @param totalTrimLengthFeet - Total linear feet of all trims
 * @returns Number of trim screws needed
 */
export function calculateTrimScrews(totalTrimLengthFeet: number): number {
  if (totalTrimLengthFeet <= 0) {
    return 0;
  }

  // 1 screw per 2 feet = 0.5 screws per foot
  return Math.ceil(totalTrimLengthFeet * SCREWS_PER_TRIM_FOOT);
}

/**
 * Calculate screws required for panel installation.
 *
 * Rule: 1 screw per 2 square feet of panel
 *
 * @param totalPanelSqft - Total square footage of all panels
 * @returns Number of panel screws needed
 */
export function calculatePanelScrews(totalPanelSqft: number): number {
  if (totalPanelSqft <= 0) {
    return 0;
  }

  // 1 screw per 2 sqft = 0.5 screws per sqft
  return Math.ceil(totalPanelSqft * SCREWS_PER_PANEL_SQFT);
}

/**
 * Calculate all screw requirements from a Bill of Materials.
 *
 * @param bom - Complete Bill of Materials
 * @returns Screw requirement breakdown
 */
export function calculateScrewsFromBOM(bom: BillOfMaterials): ScrewRequirement {
  // Collect all trim items. Legacy J-trim arrays stay alongside Snap-In so saved
  // estimates from before the Snap-In rollout still contribute to screw counts.
  const allTrimItems: MaterialItem[] = [
    ...bom.hTrims,
    ...(bom.jTrimsSnapIn ?? []),
    ...bom.jTrimsHalfInch,
    ...bom.jTrimsThreeQuarterInch,
  ];

  // Collect all panel items (W&CB and SlatWall)
  const allPanelItems: MaterialItem[] = [
    ...bom.wallAndCeilingBoard,
    ...bom.slatwall,
  ];

  // Calculate total lengths/areas
  const totalTrimLength = calculateTotalTrimLength(allTrimItems);
  const totalPanelSqft = calculateTotalPanelSqft(allPanelItems);

  // Calculate screw counts
  const trimScrews = calculateTrimScrews(totalTrimLength);
  const panelScrews = calculatePanelScrews(totalPanelSqft);

  return {
    trimScrews,
    panelScrews,
    total: trimScrews + panelScrews,
    // Product info could be added here if screws are in the product table
    productNumber: undefined,
    name: 'Installation Screws',
  };
}

/**
 * Calculate screws for a single surface (ceiling or wall).
 *
 * Useful for per-surface breakdown before BOM consolidation.
 *
 * @param panels - Panel material items for the surface
 * @param trims - Trim material items for the surface
 * @returns Screw requirement for the surface
 */
export function calculateScrewsForSurface(
  panels: MaterialItem[],
  trims: MaterialItem[]
): ScrewRequirement {
  const totalTrimLength = calculateTotalTrimLength(trims);
  const totalPanelSqft = calculateTotalPanelSqft(panels);

  const trimScrews = calculateTrimScrews(totalTrimLength);
  const panelScrews = calculatePanelScrews(totalPanelSqft);

  return {
    trimScrews,
    panelScrews,
    total: trimScrews + panelScrews,
    name: 'Surface Installation Screws',
  };
}

/**
 * Format screw count for display.
 *
 * @param screwRequirement - Screw requirement object
 * @returns Formatted string for display
 */
export function formatScrewRequirement(screwRequirement: ScrewRequirement): string {
  return `${screwRequirement.total} screws (${screwRequirement.panelScrews} panel, ${screwRequirement.trimScrews} trim)`;
}

/**
 * Calculate screws with detailed breakdown by material type.
 *
 * @param bom - Complete Bill of Materials
 * @returns Detailed screw breakdown
 */
export function calculateDetailedScrewBreakdown(bom: BillOfMaterials): {
  wallAndCeilingBoard: { sqft: number; screws: number };
  slatwall: { sqft: number; screws: number };
  hTrims: { linearFeet: number; screws: number };
  jTrimsSnapIn: { linearFeet: number; screws: number };
  jTrimsHalfInch: { linearFeet: number; screws: number };
  jTrimsThreeQuarter: { linearFeet: number; screws: number };
  totalTrimScrews: number;
  totalPanelScrews: number;
  grandTotal: number;
} {
  // Panel calculations
  const wcbSqft = calculateTotalPanelSqft(bom.wallAndCeilingBoard);
  const swSqft = calculateTotalPanelSqft(bom.slatwall);

  const wcbScrews = calculatePanelScrews(wcbSqft);
  const swScrews = calculatePanelScrews(swSqft);

  // Trim calculations
  const hTrimLength = calculateTotalTrimLength(bom.hTrims);
  const jTrimSnapInLength = calculateTotalTrimLength(bom.jTrimsSnapIn ?? []);
  const jTrimHalfLength = calculateTotalTrimLength(bom.jTrimsHalfInch);
  const jTrimThreeQuarterLength = calculateTotalTrimLength(bom.jTrimsThreeQuarterInch);

  const hTrimScrews = calculateTrimScrews(hTrimLength);
  const jTrimSnapInScrews = calculateTrimScrews(jTrimSnapInLength);
  const jTrimHalfScrews = calculateTrimScrews(jTrimHalfLength);
  const jTrimThreeQuarterScrews = calculateTrimScrews(jTrimThreeQuarterLength);

  const totalTrimScrews = hTrimScrews + jTrimSnapInScrews + jTrimHalfScrews + jTrimThreeQuarterScrews;
  const totalPanelScrews = wcbScrews + swScrews;

  return {
    wallAndCeilingBoard: { sqft: wcbSqft, screws: wcbScrews },
    slatwall: { sqft: swSqft, screws: swScrews },
    hTrims: { linearFeet: hTrimLength, screws: hTrimScrews },
    jTrimsSnapIn: { linearFeet: jTrimSnapInLength, screws: jTrimSnapInScrews },
    jTrimsHalfInch: { linearFeet: jTrimHalfLength, screws: jTrimHalfScrews },
    jTrimsThreeQuarter: { linearFeet: jTrimThreeQuarterLength, screws: jTrimThreeQuarterScrews },
    totalTrimScrews,
    totalPanelScrews,
    grandTotal: totalTrimScrews + totalPanelScrews,
  };
}

export default {
  calculateTotalTrimLength,
  calculateTotalPanelSqft,
  calculateTrimScrews,
  calculatePanelScrews,
  calculateScrewsFromBOM,
  calculateScrewsForSurface,
  formatScrewRequirement,
  calculateDetailedScrewBreakdown,
};
